import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { auth as AuthGen } from '@valvur/shared-grpc';
import { UserService } from '../../service/user.service';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { merge } from 'rxjs';
import { getTakeUntilDestroyed } from '../../../../core/helpers/take-until-destroy.helper';
import { InputFieldComponent } from '../../../../shared/components/ui/input-field/input-field.component';
import { ParentButtonComponent } from '../../../../shared/components/ui/parent-button/parent-button.component';
import { isParentKycRequiredError } from '../../../identity/helpers/identity-error.helper';
import { IdentityNavigationService } from '../../../identity/services/identity-navigation.service';
import { ParentIdentityService } from '../../../identity/services/parent-identity.service';
import { KidDocumentVerifyComponent } from '../../../identity/components/kid-document-verify/kid-document-verify.component';
import { GaEventService } from '../../../../core/services/ga.service';
import {
  PARENT_ANALYTICS_LAYOUT,
  ParentAnalyticsEvents,
} from '../../../../core/analytics/parent-analytics.events';
import { FamilyKidsService } from '../../../parent-family/services/family-kids.service';

type AddKidStep = 'details' | 'verify';

@Component({
  selector: 'app-add-kid',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    TranslatePipe,
    InputFieldComponent,
    ParentButtonComponent,
    ReactiveFormsModule,
    KidDocumentVerifyComponent,
  ],
  templateUrl: './add-kid.component.html',
  styleUrl: './add-kid.component.scss',
})
export class AddKidComponent implements OnInit {
  #dialogRef = inject(MatDialogRef<AddKidComponent>);
  #userService = inject(UserService);
  #familyKids = inject(FamilyKidsService);
  #fb = inject(FormBuilder);
  #translate = inject(TranslateService);
  #identityNav = inject(IdentityNavigationService);
  #identity = inject(ParentIdentityService);
  #analytics = inject(GaEventService);
  private takeUntilDestroyed = getTakeUntilDestroyed();
  #formTick = signal(0);

  data = inject<{ user: AuthGen.User }>(MAT_DIALOG_DATA);
  kidForm!: FormGroup;

  readonly step = signal<AddKidStep>('details');
  readonly verifyKidId = signal<string | null>(null);

  isLoading = computed(() => this.#userService.createKid.isPending());
  readonly requiresDocumentVerification = computed(() =>
    this.#identity.layerEnabled(),
  );
  readonly familyBlocked = computed(
    () =>
      this.#identity.layerEnabled() &&
      this.#identity.loaded() &&
      !this.#identity.canExpandFamily(),
  );

  firstNameValidation = signal<Record<string, string>>({
    required: '',
  });

  lastNameValidation = signal<Record<string, string>>({
    required: '',
  });

  emailValidation = signal<Record<string, string>>({
    required: '',
    email: '',
  });

  birthDateValidation = signal<Record<string, string>>({
    required: '',
  });

  readonly canConfirm = computed(() => {
    this.#formTick();
    this.isLoading();
    return this.kidForm?.valid && !this.isLoading();
  });

  readonly verifyFirstName = computed(
    () => this.kidForm?.get('firstName')?.value ?? '',
  );
  readonly verifyLastName = computed(
    () => this.kidForm?.get('lastName')?.value ?? '',
  );
  readonly verifyBirthDate = computed(
    () => this.kidForm?.get('birthDate')?.value ?? '',
  );

  constructor() {
    this.initForm();
  }

  ngOnInit(): void {
    void this.#identity.whenConfigReady();
    this.loadValidationMessages();
    merge(this.kidForm.statusChanges, this.kidForm.valueChanges)
      .pipe(this.takeUntilDestroyed())
      .subscribe(() => this.#formTick.update((n) => n + 1));
    this.#formTick.update((n) => n + 1);
  }

  discard(): void {
    this.#dialogRef.close();
  }

  async sendInvite(): Promise<void> {
    if (!this.canConfirm()) {
      return;
    }

    await this.#identity.whenConfigReady();
    if (this.familyBlocked()) {
      this.discard();
      void this.#identityNav.navigateToVerification();
      return;
    }

    try {
      await this.#userService.createKid.mutateAsync(this.kidForm.value);

      this.#analytics.sendEvent(ParentAnalyticsEvents.ADD_KID, {
        layout: PARENT_ANALYTICS_LAYOUT,
        requires_verification: this.requiresDocumentVerification(),
      });

      if (!this.requiresDocumentVerification()) {
        this.discard();
        return;
      }

      await this.#familyKids.refetch();

      const formValue = this.kidForm.value;
      const kid = this.#familyKids.unverifiedChildren().find(
        (child) =>
          child.firstName === formValue.firstName &&
          child.lastName === formValue.lastName,
      );

      if (!kid?.id) {
        this.discard();
        return;
      }

      this.verifyKidId.set(kid.id);
      this.step.set('verify');
    } catch (error) {
      if (isParentKycRequiredError(error)) {
        this.discard();
        await this.#identityNav.navigateToVerification();
      }
    }
  }

  async onVerificationCompleted(): Promise<void> {
    await this.#userService.reloadProfile();
    await this.#familyKids.refetch();
    this.discard();
  }

  startParentVerification(): void {
    this.discard();
    void this.#identityNav.navigateToVerification();
  }

  private initForm(): void {
    this.kidForm = this.#fb.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      birthDate: ['', [Validators.required]],
    });
  }

  private loadValidationMessages(): void {
    this.#translate
      .get([
        'app.components.addKid.firstNameRequired',
        'app.components.addKid.lastNameRequired',
        'app.components.addKid.emailRequired',
        'app.components.addKid.emailInvalid',
        'app.components.addKid.birthDateRequired',
      ])
      .pipe(this.takeUntilDestroyed())
      .subscribe((translations) => {
        this.firstNameValidation.set({
          required: translations['app.components.addKid.firstNameRequired'],
        });
        this.lastNameValidation.set({
          required: translations['app.components.addKid.lastNameRequired'],
        });
        this.emailValidation.set({
          required: translations['app.components.addKid.emailRequired'],
          email: translations['app.components.addKid.emailInvalid'],
        });
        this.birthDateValidation.set({
          required: translations['app.components.addKid.birthDateRequired'],
        });
      });
  }
}
